/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable sonarjs/cognitive-complexity */
'use client';

import { BackArrow, Chevron } from '@ahamove/icons';
import { formatCurrency } from '@ahm/common-helpers';
import { useLocationStore } from '@ahm/react-map/store';
import {
  ActionIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Modal,
  Popover,
  ScrollArea,
  Skeleton,
  Space,
  Stack,
  Text,
  Title,
  rem,
} from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { ChevronLeft } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useState } from 'react';
import ModalWrapper from '@/components/modals/modal-wrapper';
import TruckPackageDetailsForm from '@/components/modals/package-details-modal/truck-package-details-form';
import { ROUTES } from '@/config/router';
import { useTruckService } from '@/features/booking/components/services/use-truck-service';
import { useDetectBanHour } from '@/hooks';
import { areAllServicesRestrictedByBan } from '@/lib/utils';
import { useLocaleSuffix, usePathname } from '@/navigation';
import { TruckGroup } from '@/stores/booking/booking-store';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

const DEFAULT_TRUCK_PACKAGE_DETAIL_ITEM = {
  weight: null,
  length: null,
  width: null,
  height: null,
  quantity: 1,
  image_url: [],
};

interface TruckPackageForm {
  weight?: number | null;
  length?: number | null;
  width?: number | null;
  height?: number | null;
  quantity: number;
  image_url: string[] | string;
}

export default function TruckGroupServices() {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const localeSuffix = useLocaleSuffix();
  const tBooking = useTranslations('booking');
  const pathname = usePathname();

  const [openSelectGroupService, setOpenSelectGroupService] = useState(false);
  // const [openedModal, { open: openModal, close: closeModal }] =
  //   useDisclosure(false);

  const {
    selectedService,
    isLoadingStore,
    setTruckSelectedGroup,
    truckSelectedGroup,
    setSelectedService,
    packageDetail,
    setIsSuggestPromotion,
  } = useBookingStore((state) => state);

  const { paths } = useLocationStore((state) => state);

  const { checkServiceInBanFn } = useDetectBanHour();

  const { pickupPath, isLoading, isDisabled, truckGroupedServices } =
    useTruckService();

  const initialPackageForm = useMemo(
    () => ({
      package_detail: packageDetail?.length
        ? packageDetail
        : [DEFAULT_TRUCK_PACKAGE_DETAIL_ITEM],
    }),
    [packageDetail]
  );

  const openPopupSelectGroupService = useCallback(() => {
    setOpenSelectGroupService(true);
  }, []);

  const closePopupSelectGroupService = useCallback(() => {
    setOpenSelectGroupService(false);
  }, []);

  // const handleCloseTruckPackageDetailsModal = useCallback(() => {
  //   closeModal();
  //   openPopupSelectGroupService();
  // }, [closeModal, openPopupSelectGroupService]);

  const group = useMemo(
    () => truckGroupedServices?.[truckSelectedGroup],
    [truckGroupedServices, truckSelectedGroup]
  );

  const firstServiceSameDayVehicleInfo = useMemo(() => {
    const sameDayService = truckGroupedServices[TruckGroup.sameday] || [];
    if (sameDayService?.services?.length) {
      const services = sameDayService?.services || [];
      // Find the first available service (not in ban hour)
      const firstAvailableService = services.find(
        (service) => !checkServiceInBanFn(service)
      );
      // Return vehicle info of first available service or first service if all are banned
      return (
        firstAvailableService?.vehicle_info ||
        services[0]?.vehicle_info ||
        undefined
      );
    }
    return undefined;
  }, [truckGroupedServices, checkServiceInBanFn]);
  const handleSelectAvailableService = (group) => {
    // Automatically select the first available service in the selected group
    const services = truckGroupedServices?.[group]?.services || [];
    if (services.length > 0) {
      const availableTruckService = services.find(
        (service) => !checkServiceInBanFn(service)
      );

      if (availableTruckService) {
        setSelectedService(availableTruckService);
      }
    }
  };

  const handleSelectGroup = useCallback(
    (groupName: TruckGroup) => {
      // if (groupName === TruckGroup.sameday && !packageDetail.length) {
      //   closePopupSelectGroupService();
      //   openModal();
      //   return;
      // }

      setTruckSelectedGroup(groupName);
      handleSelectAvailableService(groupName);
      setIsSuggestPromotion(true);
      closePopupSelectGroupService();
    },
    [
      truckGroupedServices,
      setTruckSelectedGroup,
      setSelectedService,
      packageDetail,
      checkServiceInBanFn,
      closePopupSelectGroupService,
      // openModal,
    ]
  );

  const renderTruckGroupedServicesModal = useCallback(() => {
    if (!truckGroupedServices) return null;

    return Object.values(truckGroupedServices).map((values) => {
      const isSelectedGroup = truckSelectedGroup === values?.group_name_en_us;
      const isDisabled = areAllServicesRestrictedByBan(values.services);

      return (
        <Box py={4} key={values?.group_name_en_us}>
          <Button
            fullWidth
            variant="white"
            color="neutral"
            mih={rem(66)}
            h="auto"
            radius={12}
            p="sm"
            onClick={() => handleSelectGroup(values?.group_name_en_us)}
            disabled={isDisabled}
            bg={isSelectedGroup ? 'primary.0' : undefined}
            styles={{
              root: {
                position: 'relative',
                border: `1px solid ${isSelectedGroup ? 'var(--mantine-color-primary-4)' : 'var(--mantine-color-neutral-4)'}`,
              },
              inner: {
                justifyContent: 'flex-start',
              },
              label: {
                width: '100%',
                flex: '1 1 auto',
                whiteSpace: 'pre-line',
              },
            }}
            leftSection={
              <Avatar
                radius="sm"
                variant="outline"
                size={rem(32)}
                p={0}
                flex="none"
                src={values?.icon}
                alt={values?.group_name_en_us}
                styles={{
                  image: { objectFit: 'contain' },
                  placeholder: {
                    border: 'none',
                  },
                }}
              />
            }
            rightSection={
              paths?.length > 1 ? (
                <Text
                  style={{ whiteSpace: 'nowrap' }}
                  fw={700}
                  size="sm"
                  c={isSelectedGroup ? 'primary.4' : 'neutral.7'}
                >
                  Từ {formatCurrency(values?.estimated_price)}
                </Text>
              ) : null
            }
          >
            <Stack gap={rem(2)} w="100%" justify="flex-start">
              <Group w="100%" gap={rem(4)}>
                <Title order={5} fw={600} c="neutral.7">
                  {values?.[`group_name_${localeSuffix}`]}
                </Title>
              </Group>

              <Text c="neutral.6" size="xs" fw={500} ta="left">
                {values?.group_description}
              </Text>
            </Stack>
          </Button>
        </Box>
      );
    });
  }, [
    handleSelectGroup,
    localeSuffix,
    truckGroupedServices,
    truckSelectedGroup,
  ]);

  const renderSkeleton = useCallback(
    () => (
      <Card radius={rem(8)}>
        <Group gap="xs" align="center">
          <Skeleton height={50} circle />
          <Stack gap={rem(4)} w={'100%'} maw={rem(300)}>
            <Skeleton height={20} radius="xl" w="80%" />
            <Skeleton height={20} radius="xl" w="60%" />
          </Stack>
        </Group>
      </Card>
    ),
    []
  );

  const isAllServicesRestricted = useMemo(
    () =>
      !truckGroupedServices?.[truckSelectedGroup]?.services?.length ||
      areAllServicesRestrictedByBan(
        truckGroupedServices?.[truckSelectedGroup]?.services
      ),
    [truckGroupedServices, truckSelectedGroup]
  );

  if (isAllServicesRestricted) return null;
  if (isLoading) return renderSkeleton();

  const ServiceButton = (
    <Button
      loading={isLoadingStore}
      disabled={isDisabled}
      fullWidth
      h="auto"
      mih={isMobile ? rem(48) : rem(56)}
      radius={rem(12)}
      pl={isMobile ? rem(8) : rem(12)}
      py={isMobile ? rem(8) : rem(12)}
      onClick={() => {
        setOpenSelectGroupService(!openSelectGroupService);
      }}
      px="md"
      variant="white"
      leftSection={
        <Avatar
          radius="sm"
          variant="outline"
          size={rem(32)}
          p={0}
          flex="none"
          src={group?.icon}
          alt={group?.group_name_en_us}
          styles={{
            image: { objectFit: 'contain' },
            placeholder: {
              border: 'none',
            },
          }}
        />
      }
      rightSection={
        isMobile ? (
          <Chevron className="size-5 md:size-6" />
        ) : (
          <Text
            fw={700}
            size="sm"
            c="primary.6"
            style={{ whiteSpace: 'nowrap' }}
          >
            Đổi dịch vụ
          </Text>
        )
      }
      justify="space-between"
      styles={{
        root: {
          overflow: 'visible',
          position: 'relative',
        },
        label: {
          width: '100%',
        },
      }}
    >
      <Stack gap={rem(4)} ta="left" w="100%">
        {pickupPath ? (
          <>
            <Title
              order={isMobile ? 6 : 4}
              fw={700}
              c="neutral.6"
              className="truncate"
            >
              {group[`group_name_${localeSuffix}`]}
            </Title>
            {group?.group_description ? (
              <Text size="sm" fw={500} c="neutral.7" truncate>
                {group.group_description}
              </Text>
            ) : null}
          </>
        ) : null}
      </Stack>
      {selectedService?.badge ? (
        <Badge
          tt="capitalize"
          size="md"
          radius="sm"
          pos="absolute"
          fw={600}
          top={rem(-8)}
          right={rem(-6)}
          bg={selectedService.badge.background_color ?? 'red.6'}
          c={selectedService.badge.text_color ?? 'white'}
        >
          {selectedService.badge[`description_${localeSuffix}`] || 'Mới'}
        </Badge>
      ) : null}
    </Button>
  );

  if (isMobile) {
    return (
      <>
        {ServiceButton}

        <ModalWrapper
          title={tBooking('service.choose_service')}
          isOpen={openSelectGroupService}
          onClose={closePopupSelectGroupService}
          optionStyle={{
            headerStyle: {
              minHeight: rem(56),
              borderBottom: `1px solid var(--mantine-color-neutral-0)`,
              paddingLeft: rem(16),
            },
            bodyStyle: {
              paddingBottom: 16,
            },
          }}
        >
          {renderTruckGroupedServicesModal()}
        </ModalWrapper>

        {/* <Modal
          opened={openedModal}
          onClose={handleCloseTruckPackageDetailsModal}
          title={
            <Group gap="xl" align="center" justify="space-between" w="100%">
              <ActionIcon
                size={rem(24)}
                onClick={handleCloseTruckPackageDetailsModal}
                variant="transparent"
              >
                <BackArrow width={24} height={24} />
              </ActionIcon>
              <Title order={2} size="h4" flex={1} ta="center">
                Thông tin hàng hóa
              </Title>
            </Group>
          }
          withCloseButton={false}
          fullScreen
          closeOnEscape={false}
          scrollAreaComponent={ScrollArea.Autosize}
          styles={{
            header: {
              backgroundColor: 'white',
              minHeight: rem(44),
              boxShadow: 'none',
              padding: `${rem(10)} ${rem(16)}`,
              border: `1px solid var(--mantine-color-neutral-1)`,
            },
            title: {
              width: '100%',
            },
            content: {
              backgroundColor: 'var(--mantine-color-neutral-0)',
            },
            body: {
              padding: 0,
              minHeight: 600,
            },
          }}
        >
          <TruckPackageDetailsForm
            innerProps={{
              initialData: initialPackageForm as unknown as TruckPackageForm[],
              group: TruckGroup.sameday,
              firstServiceSameDayVehicleInfo,
              truckGroupedServices,
            }}
            onClose={closeModal}
          />
        </Modal> */}
      </>
    );
  }

  return (
    <>
      <Popover
        opened={openSelectGroupService}
        onChange={closePopupSelectGroupService}
        styles={{
          dropdown: {
            filter: 'drop-shadow(0px 16px 48px rgba(0, 0, 0, 0.08))',
            borderRadius: 12,
            background: 'white',
            padding: 0,
            border: 'none',
            overflow: 'hidden',
          },
        }}
        zIndex={200}
        width={400}
        offset={16}
        position="right"
        shadow="md"
      >
        <Popover.Target>{ServiceButton}</Popover.Target>

        <Popover.Dropdown>
          <Group
            pos="sticky"
            top={0}
            left={0}
            justify="flex-start"
            h={rem(60)}
            w="100%"
            p="md"
            bg="white"
            styles={{
              root: {
                zIndex: 100,
                borderBottom: `1px solid var(--mantine-color-neutral-1)`,
              },
            }}
          >
            <ActionIcon
              size={rem(34)}
              variant="transparent"
              onClick={closePopupSelectGroupService}
              c="neutral.5"
            >
              <ChevronLeft width={24} height={24} />
            </ActionIcon>
            <Title lh={rem(28)} order={3}>
              {tBooking('service.choose_service')}
            </Title>
          </Group>
          <ScrollArea.Autosize
            px={16}
            py={12}
            mah={
              pathname === ROUTES.nationwide
                ? rem(680 - 65)
                : 'calc(100vh - 65px)'
            }
            h="auto"
          >
            {renderTruckGroupedServicesModal()}
          </ScrollArea.Autosize>
        </Popover.Dropdown>
      </Popover>

      {/* <Modal
        opened={openedModal}
        onClose={handleCloseTruckPackageDetailsModal}
        title={
          <Group gap="xl" align="center" justify="space-between" w="100%">
            {isMobile ? (
              <ActionIcon
                size={rem(24)}
                onClick={closeModal}
                variant="transparent"
              >
                <BackArrow width={24} height={24} />
              </ActionIcon>
            ) : null}
            <Title
              order={2}
              size="h4"
              flex={1}
              ta={isMobile ? 'center' : 'left'}
            >
              Thông tin hàng hóa
            </Title>
            <Space />
          </Group>
        }
        withCloseButton={!isMobile}
        fullScreen={isMobile}
        closeOnEscape={false}
        scrollAreaComponent={ScrollArea.Autosize}
        styles={{
          header: {
            backgroundColor: 'white',
            minHeight: rem(44),
            padding: isMobile
              ? `${rem(10)} ${rem(16)}`
              : `${rem(16)} ${rem(16)}`,
            border: isMobile
              ? `1px solid var(--mantine-color-neutral-1)`
              : 'none',
          },
          title: {
            width: '100%',
          },
          close: {
            '--cb-icon-size': rem(32),
            height: rem(32),
            width: rem(32),
            borderRadius: rem(32),
          },
          content: {
            backgroundColor: 'white',
          },
          body: {
            padding: 0,
            minHeight: 500,
          },
        }}
      >
        <TruckPackageDetailsForm
          innerProps={{
            initialData: initialPackageForm as unknown as TruckPackageForm[],
            group: TruckGroup.sameday,
            firstServiceSameDayVehicleInfo,
            truckGroupedServices,
          }}
          onClose={closeModal}
        />
      </Modal> */}
    </>
  );
}
